import { createContext, useContext, useState, useCallback, useRef } from 'react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';

const ConfirmContext = createContext(() => Promise.resolve(false));

export function ConfirmProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [opts, setOpts] = useState({
    title: 'هل أنت متأكد؟',
    description: '',
    confirmText: 'نعم، تأكيد',
    cancelText: 'لا، إلغاء',
    danger: false,
  });
  const resolverRef = useRef(null);

  const confirm = useCallback((options = {}) => {
    setOpts({
      title: options.title || 'هل أنت متأكد؟',
      description: options.description || '',
      confirmText: options.confirmText || 'نعم، تأكيد',
      cancelText: options.cancelText || 'لا، إلغاء',
      danger: options.danger !== false, // default true (delete is destructive)
    });
    setOpen(true);
    return new Promise((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const handleClose = (result) => {
    setOpen(false);
    if (resolverRef.current) {
      resolverRef.current(result);
      resolverRef.current = null;
    }
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      <AlertDialog open={open} onOpenChange={(o) => !o && handleClose(false)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800 text-white" dir="rtl" data-testid="confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-right text-lg">{opts.title}</AlertDialogTitle>
            {opts.description && (
              <AlertDialogDescription className="text-right text-zinc-300 mt-2 whitespace-pre-line">
                {opts.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-row-reverse gap-2">
            <AlertDialogAction
              onClick={() => handleClose(true)}
              className={opts.danger
                ? 'bg-[#ff3b30] hover:bg-[#dc2626] text-white'
                : 'bg-[#34c759] hover:bg-[#28a745] text-white'}
              data-testid="confirm-yes"
            >
              {opts.confirmText}
            </AlertDialogAction>
            <AlertDialogCancel
              onClick={() => handleClose(false)}
              className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700 text-white"
              data-testid="confirm-no"
            >
              {opts.cancelText}
            </AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </ConfirmContext.Provider>
  );
}

/**
 * useConfirm() => returns an async function:
 *   const confirm = useConfirm();
 *   const ok = await confirm({ title, description, confirmText, cancelText, danger });
 *   if (!ok) return;
 */
export function useConfirm() {
  return useContext(ConfirmContext);
}
